import axios from "axios";
import { PATH } from "../../utils/config/constants";
import { tokenStorage } from "../auth/tokenStorage";
import { API_ENDPOINTS } from "../../utils/config/constants";

const apiClient = axios.create({
    baseURL: PATH,
});

const authEndpoints = [
    API_ENDPOINTS.LOGIN,
    API_ENDPOINTS.LOGIN_VERIFY_CODE,
    API_ENDPOINTS.PASSWORD_RESET_REQUEST,
    API_ENDPOINTS.PASSWORD_RESET_CONFIRM,
    API_ENDPOINTS.REGISTER,
    API_ENDPOINTS.TELEGRAM,
    API_ENDPOINTS.REFRESH,
];

const isAuthEndpoint = (url?: string) =>
    Boolean(url && authEndpoints.some((endpoint) => url.split("?")[0].endsWith(endpoint)));

let isRefreshing = false;
let pendingRequests: Array<{ resolve: (token: string) => void; reject: (err: unknown) => void }> = [];

apiClient.interceptors.request.use((config) => {
    const accessToken = tokenStorage.getAccessToken();
    if (accessToken) {
        config.headers = config.headers ?? {};
        config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
});

apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (error.response?.status !== 401 || originalRequest?._retry || isAuthEndpoint(originalRequest?.url)) {
            return Promise.reject(error);
        }

        if (!originalRequest) {
            return Promise.reject(error);
        }

        if (isRefreshing) {
            return new Promise<string>((resolve, reject) => {
                pendingRequests.push({ resolve, reject });
            }).then((token) => {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                return apiClient(originalRequest);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
            const response = await axios.post<{ accessToken: string }>(
                `${PATH}${API_ENDPOINTS.REFRESH}`,
                {},
                { withCredentials: true }
            );

            const newToken = response.data.accessToken;
            tokenStorage.setAccessToken(newToken);

            pendingRequests.forEach((p) => p.resolve(newToken));
            pendingRequests = [];

            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return apiClient(originalRequest);
        } catch (refreshError) {
            pendingRequests.forEach((p) => p.reject(refreshError));
            pendingRequests = [];

            tokenStorage.clear();
            window.location.href = '/';
            return Promise.reject(refreshError);
        } finally {
            isRefreshing = false;
        }
    }
);

export default apiClient;
